import { useState, useEffect, useCallback } from "react";
import { Bug } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { BugReportDialog } from "./BugReportDialog";
import { useBugReport } from "@/hooks/useBugReport";
import { useBugReportNotifications } from "@/hooks/useBugReportNotifications";

interface BugReportButtonProps {
  sidebarWidth?: number;
}

export function BugReportButton({ sidebarWidth = 0 }: BugReportButtonProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [screenshotDataUrl, setScreenshotDataUrl] = useState<string | null>(null);
  const { captureScreenshot, submitReport, isSubmitting } = useBugReport();
  const { unresolvedCount } = useBugReportNotifications();

  const handleOpen = useCallback(async () => {
    // Capture screenshot BEFORE opening dialog so the dialog itself isn't in it
    const screenshot = await captureScreenshot();
    setScreenshotDataUrl(screenshot);
    setDialogOpen(true);
  }, [captureScreenshot]);

  // Keyboard shortcut: Ctrl+Shift+B
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === "B") {
        e.preventDefault();
        handleOpen();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleOpen]);

  const handleSubmit = async (params: {
    description?: string;
    impact?: "critical" | "error" | "warning";
    screenshotDataUrl?: string | null;
  }) => {
    await submitReport(params);
    setDialogOpen(false);
    setScreenshotDataUrl(null);
  };

  const handleOpenChange = (open: boolean) => {
    setDialogOpen(open);
    if (!open) setScreenshotDataUrl(null);
  };

  return (
    <>
      <div
        className="fixed bottom-4 z-40 transition-all duration-300"
        style={{ left: `${sidebarWidth + 16}px` }}
        data-bug-report="true"
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="outline"
              className="h-10 w-10 rounded-full shadow-lg bg-background/80 backdrop-blur-sm hover:bg-background border-border/50 hover:border-border transition-all"
              onClick={handleOpen}
              aria-label="Report a bug"
            >
              <Bug className="h-4.5 w-4.5 text-muted-foreground" />
              {unresolvedCount > 0 && (
                <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary text-[10px] font-medium text-primary-foreground flex items-center justify-center">
                  {unresolvedCount > 9 ? "9+" : unresolvedCount}
                </span>
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">
            <p>Report an issue <kbd className="ml-1.5 text-[10px] bg-muted px-1 py-0.5 rounded">Ctrl+Shift+B</kbd></p>
          </TooltipContent>
        </Tooltip>
      </div>

      <BugReportDialog
        open={dialogOpen}
        onOpenChange={handleOpenChange}
        screenshotDataUrl={screenshotDataUrl}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      />
    </>
  );
}
